import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import { Box, useTheme } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { themeSettings, tokens } from "../../../../theme";
import Shbutton from "../../../theme/buttons/Shbutton";
import Sidepanel2 from "../../../theme/sidepanel2/Sidepanel2";
import SidepanelHeaderActiongroup from "../../../theme/sidepanel2/SidepanelHeaderActiongroup";
import Subheading from "../../../theme/text/Subheading";
import Text from "../../../theme/text/Text";
import { ENV_TYPES } from "../../Sidebar";

// Other imports
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import { useGetOrderQuery, useUpdateOrderMutation, useSetOrderCostsMutation, useUpdatePickupOrderMutation, useResyncOrderMutation, useUpdateOrderPickingTypeMutation, useUpdatePriorityMutation, useUpdateOrderFieldsMutation, useSetOrderToShippedMutation, useGetOrderLinesQuery, useSplitOrderMutation, useCreateOrderEventMutation } from "../../../../newapi/order/ordersSlice";
import { getOrderStateData } from "../../../../utils/labelColorText";
import { general_states } from "../../../../utils/staticEnums";
import ButtonGroup from "../../../theme/buttons/ButtonGroup";
import MSplitOrder from "../../Modals/MSplitOrder";
import MSplitBackorder from "../../Modals/MSplitBackorder";
import MUpdateOrderState from "../../Modals/MUpdateOrderState";
import { spOrderDetailsConfig } from "./spOrderDetailsConfig";
import MAddInvoiceLine from "../../Modals/MAddInvoiceLine";

// Views imports
import { useSelector } from "react-redux";
import { selectUserPermission } from "../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from "../../../../utils/permissions";
import { showToastMessage, showToastMessagePostRequest, showToastMessageRequestError, showToastMessageUpdateRequest } from "../../../../utils/toasts";
import ShDateRangePicker from "../../../theme/ShDateRangePicker";
import MAddShipment from '../../Modals/MAddShipment';
import MpAddReturn from '../../ModalsProgression/MpAddReturn/MpAddReturn';
import Logs from "./Views/Logs";
import SummaryRelation from "./Views/SummaryRelation";
import PickingLines from "./Views/PickingLines";
import Shipments from "./Views/Shipments";
import ShipOrder from "./Views/ShipOrder";
import Summary from './Views/Summary';
import AMOrderSpWarehouses, { AMOrderSpRelations } from "../../Modals/ActionModal/AMOrderSp";
import MAddClaim from "../../Modals/MAddClaim";
import MpDuplicateOrder from "../../ModalsProgression/MpDuplicateOrder/MpDuplicateOrder";
import MConfirmation from '../../Modals/MConfirmation';
import MConfirmationWithValue from '../../Modals/MConfirmationWithValue';
import ShLabel from '../../../theme/label/ShLabel';
import SHQuickAction from '../../../theme/Modal/SHQuickAction';
import MEditOrderExtraFields from '../../Modals/MEditOrderExtraFields';
import MLoader from '../../Modals/MLoader';
import MDynamicValues from '../../Modals/MDynamicValues';
import MSnoozeOrder from '../../Modals/MSnoozeOrder';
import MCheckOrderInvoiceProducts from '../../Modals/MCheckOrderInvoiceProducts';
import MCheckCostRule from '../../Modals/MCheckCostRule';
import MOrderWebshopJson from '../../Modals/MOrderWebshopJson';

const SidePanelActions = (props) => {
    const {t} = useTranslation();
    
    const [addModalOpen, setAddModalOpen] = useState(false);
    const addModal = {
        open: addModalOpen,
        setOpen: setAddModalOpen
    }
    const handleClose =(split, ie) => {
        addModal.setOpen(false);
    }
    
    return (
        <SidepanelHeaderActiongroup>
                <Shbutton isCancel={true} 
                    onClick={() => props.setIsExtended(false)} 
                    color="primary" 
                    variant="text" 
                    startIcon={<ArrowRightAltIcon/>}
                >
                    Cancel
                </Shbutton>
                {props.withoutCosts && (
                <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle", mr: 1 }}>
                    <ShLabel size={32} fitted palette="normal" variant="orange">
                        {t('order_no_costs_label')}
                    </ShLabel>
                </Box>
                )}
                {(props.shouldShowActions || props.shouldShowRelationActions) &&
                    <ButtonGroup version={2} isSidepanel>
                        <Shbutton
                            onClick={props.handleFilterPopoverClick}
                            className={"group-btn"}
                            // onClick={props.action}
                            color={"primaryGrey"}
                            variant="contained"
                        >
                            {t('actions')}
                        </Shbutton>
                    </ButtonGroup>
                }
        </SidepanelHeaderActiongroup>
    )
}

const SpOrderDetails = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // const { data: shippers } =  ({warehouseId: props.data.warehouseId},
    //     {
    //         skip: !props.data.warehouseId
    //     }
    // );


    const { t, ready, i18n } = useTranslation();
    const hasModifyPrivilege = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.OUTBOUND_MANAGE,UserPrivileges.RP_ORDER_MANAGE])
    );
    const hasModifyShippedPrivilege = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.OUTBOUND_MODIFY_SHIPPED_STATE])
    );

    const hasCancelPickedPrivilege = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.OUTBOUND_CANCEL_PICKED])
    );

    const hasCreateReturnPermission = useSelector((state) => 
        selectUserPermission(state, [UserPrivileges.RETURN_MANAGE])
    );

    const hasInvoicePrivilege = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.INVOICE_MANAGE])
    );

    const selectedEnvironmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const isRelation = selectedEnvironmentType === ENV_TYPES[200];

    const { data: orderData, isLoading,isFetching,isError } = useGetOrderQuery(props.orderId,
        {
            skip: !props.orderId
        });

    // Reuse the same orderlines query the summary views already load, so this hits the
    // shared RTK Query cache instead of firing an extra request.
    const { data: orderLinesData } = useGetOrderLinesQuery({
        args: {
            id: props.orderId,
            page: 1,
            size: 1000,
            body: {}
        }
    }, {
        skip: !props.orderId
    });

    // Backordered lines flattened into split payloads (amount = backordered quantity).
    // Virtual products expose their backorder on the children, regular lines on the line itself.
    const backorderProducts = useMemo(() => {
        const products = [];
        (orderLinesData?.data ?? []).forEach(line => {
            if (line.children && line.children.length > 0) {
                line.children.forEach(child => {
                    if (child.type !== "add_on" && child.backorder > 0) {
                        products.push({ orderLineId: child.id, amount: child.backorder });
                    }
                });
            } else if (line.type !== "add_on" && line.backorder > 0) {
                products.push({ orderLineId: line.id, amount: line.backorder });
            }
        });
        return products;
    }, [orderLinesData]);

    const isDatePlannedInFuture = (datePlanned) => {
        if (!datePlanned) return false;
        const plannedDate = new Date(datePlanned);
        const now = new Date();
        return plannedDate > now;
    };

    const defaultIcon = useMemo(() => ({
        theme: theme.palette.mode === "dark" ? "blue" : "primary",
        content: <ImportExportIcon/>
    }), [theme.palette.mode]);

    const defaultOptions = useMemo(() => [
        // { component: <ShRowSkeleton heading={t("total_products")} /> },
        // { component: <ShRowSkeleton heading={t("total_locations")} /> }
    ], [t]);
    
    
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const filterPopoverOpen = Boolean(filterAnchorEl);
    const filterPopoverId = filterPopoverOpen ? 'simple-popover' : undefined;
    const [orderRemark, setOrderRemark] = useState("");

    const [selectedOrders, setSelectedOrders] = useState([]);
    useEffect(() => {
        if (props.orderId) {
            setSelectedOrders([props.orderId]);
        }
    }, [props.orderId]);

    useEffect(() => {
        if (orderData) {
            setOrderRemark(orderData.remark);
        }
    }, [orderData])

    const handleFilterPopoverClick = (event) => {
        setFilterAnchorEl(event.currentTarget);
    };

    const handleFilterPopoverClose = () => {
        setFilterAnchorEl(null);
        // setExportAnchorEl(null);
    };

    const setIsExtendedCallback = useCallback(() => props.setIsExtended(false), [props.setIsExtended]);

    const shouldShowActions =
        (orderData && ![general_states.SHIPPED, general_states.FULLYPICKED, general_states.PICKING_IN_PROGRESS, general_states.CANCELLED].includes(orderData?.state) &&
            orderData.amountOfProducts > 1 && hasModifyPrivilege) ||
        hasModifyPrivilege ||
        (hasInvoicePrivilege && orderData?.state === general_states.SHIPPED) ||
        (hasCreateReturnPermission && orderData?.state === general_states.SHIPPED) ||
        (hasCreateReturnPermission && orderData?.state !== general_states.CANCELLED && orderData?.state !== general_states.SHIPPED);
    const shouldShowRelationActions =     
        (orderData && ![general_states.SHIPPED, general_states.FULLYPICKED, general_states.PICKING_IN_PROGRESS, general_states.CANCELLED].includes(orderData?.state) &&
        orderData.amountOfProducts > 1 && hasModifyPrivilege) ||
        hasModifyPrivilege ||
        (hasCreateReturnPermission && orderData?.state === general_states.SHIPPED) ||
        (hasCreateReturnPermission && orderData?.state !== general_states.CANCELLED && orderData?.state !== general_states.SHIPPED)

    // Memoize the default state to avoid recalculating it on every render
    const defaultState = useMemo(() => ({
        title: "",
        subTitle: "",
        icon: defaultIcon,
        options: defaultOptions,
        actions: <SidePanelActions shouldShowActions={shouldShowActions} shouldShowRelationActions={shouldShowRelationActions} handleFilterPopoverClick={handleFilterPopoverClick} orderId={props.orderId} state={orderData?.state} setIsExtended={setIsExtendedCallback}/>,
        styling: {
            header : {
                padding:"0px !important",
            }
        },
    }), [defaultIcon, defaultOptions, shouldShowActions, shouldShowRelationActions, props.orderId, orderData?.state, setIsExtendedCallback]);

    const [panel, setPanel] = useState(defaultState);

    useEffect(() => {
        setPanel(defaultState);
    }, [isFetching,props.strategyId,theme.palette.mode])
    

    const getBackgroundColor = useCallback((state,contrast=400) => {
        const {labelcolor,labeltext} = getOrderStateData(state, theme, isRelation);

        if(labelcolor === "grey") {
            if(theme.palette.mode === "dark") {
                return colors["grey"][300];
            } else {
                return colors["grey"][200];
            }
        }
        return colors[labelcolor][contrast];
    });

    const getColor = useCallback((state,contrast=400) => {
        const {labelcolor,labeltext} = getOrderStateData(state, theme, isRelation);
        if(labelcolor === "grey") {
            return colors["txt"]["primary"];
        }
        return colors[labelcolor][contrast];
    });
    
    const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
    const duplicateModal = {
        open: duplicateModalOpen,
        setOpen: setDuplicateModalOpen
    };

    const handleDuplicateClose = () => {
        duplicateModal.setOpen(false);
    };

    // Opens the freshly duplicated order in a stacked sidepanel.
    const [createdOrderId, setCreatedOrderId] = useState();
    const [createdOrderExtended, setCreatedOrderExtended] = useState(false);
    const handleOpenCreatedOrder = (orderId) => {
        setCreatedOrderId(orderId);
        setCreatedOrderExtended(true);
    };

    const [confirmPickupModalOpen, setConfirmPickupModalOpen] = useState(false);
    const confirmPickupModal = {
        open: confirmPickupModalOpen,
        setOpen: setConfirmPickupModalOpen,
    };

    const handleConfirmPickupClose = () => {
        confirmPickupModal.setOpen(false);
    };

    const [loaderModalOpen, setLoaderModalOpen] = useState(false);
    const loaderModal = {
        open: loaderModalOpen,
        setOpen: setLoaderModalOpen,
    };

    const [updateOrderPickingType,{isLoading: isUpdatePickupTypeLoading}] = useUpdateOrderPickingTypeMutation();
    const handlePickFromBulk = async (isPickup) => {
        try {
            if (isUpdatePickupTypeLoading) return;

            loaderModal.setOpen(true);

            const data = await updateOrderPickingType({
                id:props.orderId,
                pickFromBulk:true,
            }).unwrap()
            showToastMessageUpdateRequest(t, data);
        } catch (err) {
            showToastMessageRequestError(t("create_failed"), t, err);
        } finally {
            loaderModal.setOpen(false);
        }

        handleFilterPopoverClose();
    }
    
    const [confirmShipmentModalOpen, setConfirmShipmentModalOpen] = useState(false);
    const confirmShipmentModal = {
        open: confirmShipmentModalOpen,
        setOpen: setConfirmShipmentModalOpen,
    };

    const handleConfirmShipmentClose = () => {
        confirmShipmentModal.setOpen(false);
    };

    const [updatePickupOrder,{isLoading: isUpdatePickupLoading}] = useUpdatePickupOrderMutation();
    const handleOrderPickup = async (isPickup) => {
        try {
            if (isUpdatePickupLoading) return;

            const data = await updatePickupOrder({
                id:props.orderId,
                isPickup:isPickup,
            }).unwrap()
            showToastMessageUpdateRequest(t, data);
        } catch (err) {
            showToastMessageRequestError(t("create_failed"), t, err);
        }

        handleFilterPopoverClose();
    }
    
    const [confirmPickFromBulkModalOpen, setConfirmPickFromBulkModalOpen] = useState(false);
    const confirmPickFromBulkModal = {
        open: confirmPickFromBulkModalOpen,
        setOpen: setConfirmPickFromBulkModalOpen,
    };

    const handleConfirmPickFromBulkClose = () => {
        confirmPickFromBulkModal.setOpen(false);
    };
    
    const [confirmResyncOrderModalOpen, setConfirmResyncOrderModalOpen] = useState(false);
    const confirmResyncOrderModal = {
        open: confirmResyncOrderModalOpen,
        setOpen: setConfirmResyncOrderModalOpen,
    };

    const handleResyncOrderClose = () => {
        confirmResyncOrderModal.setOpen(false);
    };

    const [webshopJsonModalOpen, setWebshopJsonModalOpen] = useState(false);
    const webshopJsonModal = {
        open: webshopJsonModalOpen,
        setOpen: setWebshopJsonModalOpen,
    };
    
    const [setToShippedModalOpen, setSetToShippedModal] = useState(false);
    const setToShippedModal = {
        open: setToShippedModalOpen,
        setOpen: setSetToShippedModal,
    };

    const handleSetToShippedModalClose = () => {
        setToShippedModal.setOpen(false);
    };

    const [setOrderToShipped,{isLoading: setToShippedLoading}] = useSetOrderToShippedMutation();
    const handleSetOrderToShipped = async (isPickup) => {
        try {
            if (setToShippedLoading) {
                return;
            }

            const data = await setOrderToShipped(props.orderId).unwrap();
            showToastMessageUpdateRequest(t, data);
        } catch (err) {
            showToastMessageRequestError(t("update_failed"), t, err);
        }

        handleFilterPopoverClose();
    }

    const [resyncOrder,{isLoading: isResyncLoading}] = useResyncOrderMutation();
    const handleOrderResync = async (isPickup) => {
        try {
            if (isResyncLoading) {
                return;
            }

            const data = await resyncOrder({
                id:props.orderId,
            }).unwrap();
            showToastMessageUpdateRequest(t, data);
            props.setIsExtended(false);
            
        } catch (err) {
            showToastMessageRequestError(t("create_failed"), t, err);
        }

        handleFilterPopoverClose();
    }
    
    const [confirmNoCostOrderModalOpen, setConfirmNoCostOrderModalOpen] = useState(false);
    const confirmNoCostOrderModal = {
        open: confirmNoCostOrderModalOpen,
        setOpen: setConfirmNoCostOrderModalOpen,
    };

    const handleConfirmNoCostOrderModalClose = () => {
        confirmNoCostOrderModal.setOpen(false);
    };

    const [updateWithoutCosts,{isLoading: isUpdateWithoutCostsLoading}] = useUpdateOrderMutation();
    const handleUupdateWithoutCosts = async (isPickup) => {
        try {
            if (isUpdateWithoutCostsLoading) return;

            const data = await updateWithoutCosts({
                id:props.orderId,
                withoutCosts: true,
            }).unwrap()
            showToastMessageUpdateRequest(t, data);
        } catch (err) {
            showToastMessageRequestError(t("update_failed"), t, err);
        }

        handleFilterPopoverClose();
    }
    
    const [priorityModalOpen, setPriorityModalOpen] = useState(false);
    const priorityModal = {
        open: priorityModalOpen,
        setOpen: setPriorityModalOpen,
    };

    const [updatePriority,{isLoading: isPriorityLoading}] = useUpdatePriorityMutation();
    const handleUpdatePriority = async (values) => {
        try {
            if (isPriorityLoading) return;

            const updateObj = {
                id: props.orderId,
                priority: values.priority
            }

            const data = await updatePriority(updateObj).unwrap();
            showToastMessageUpdateRequest(t, data);
        } catch (err) {
            showToastMessageRequestError(t("update_failed"), t, err);
        }

        handleFilterPopoverClose();
    }

    const [updateInternalRemarkModalOpen, setUpdateInternalRemarkModal] = useState(false);
    const updateInternalRemarkModal = {
        open: updateInternalRemarkModalOpen,
        setOpen: setUpdateInternalRemarkModal
    }

    const [freeReturnModalOpen, setFreeReturnModalOpen] = useState(false);
    const freeReturnModal = {
        open: freeReturnModalOpen,
        setOpen: setFreeReturnModalOpen
    }

    const handleFreeReturnClose = () => {
        freeReturnModal.setOpen(false);
    }

    const [updateOrderFields,{isLoading: isUpdateOrderFieldsLoading}] = useUpdateOrderFieldsMutation();
    const handleFreeReturn = async () => {
        try {
            if (isUpdateOrderFieldsLoading) return;

            const data = await updateOrderFields({
                id: props.orderId,
                body: { freeReturn: !orderData?.freeReturn },
            }).unwrap()
            showToastMessageUpdateRequest(t, data);
        } catch (err) {
            showToastMessageRequestError(t("update_failed"), t, err);
        }

        handleFilterPopoverClose();
    }

    const handleUpdateInternalRemark = async (values) => {
        try {
            const data = await updateOrder({
                id:props.orderId,
                relationRemark:values.relationRemark,
            }).unwrap()
            showToastMessageUpdateRequest(t, data);

        } catch (err) {
            showToastMessageRequestError(t("update_failed"), t, err);
        }
    }

    const [eventModalOpen, setEventModalOpen] = useState(false);
    const eventModal = {
        open: eventModalOpen,
        setOpen: setEventModalOpen
    }

    const [createOrderEvent,{isLoading: isCreateOrderEventLoading}] = useCreateOrderEventMutation();
    const handleCreateOrderEvent = async (values) => {
        try {
            if (isCreateOrderEventLoading) return;

            const data = await createOrderEvent({
                id: props.orderId,
                message: values.message,
            }).unwrap()
            showToastMessageUpdateRequest(t, data);
        } catch (err) {
            showToastMessageRequestError(t("update_failed"), t, err);
        }

        handleFilterPopoverClose();
    }

    const [checkModalOpen, setCheckModalOpen] = useState(false);
    const checkModal = {
        open: checkModalOpen,
        setOpen: setCheckModalOpen,
    };

    const handleCheckModalClose = () => {
        checkModal.setOpen(false);
    };

    const [checkCostRuleModalOpen, setCheckCostRuleModalOpen] = useState(false);
    const checkCostRuleModal = {
        open: checkCostRuleModalOpen,
        setOpen: setCheckCostRuleModalOpen,
    };

    const handleCheckCostRuleModalClose = () => {
        checkCostRuleModal.setOpen(false);
    };

    const confirmDuplicateModal = {
        open: false,
        setOpen: () => duplicateModal.setOpen(true),
        confirm: () => {},
        data: {}
    };
    
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [backorderSplitModalOpen, setBackorderSplitModalOpen] = useState(false);

    // The split action routes through here: when the order has backordered lines we ask
    // the backorder question first, otherwise we open the manual split modal directly.
    const addModal = {
        open: addModalOpen,
        setOpen: (val) => {
            if (!val) {
                setAddModalOpen(false);
                return;
            }
            if (backorderProducts.length > 0) {
                setBackorderSplitModalOpen(true);
            } else {
                setAddModalOpen(true);
            }
        }
    }

    const backorderSplitModal = {
        open: backorderSplitModalOpen,
        setOpen: setBackorderSplitModalOpen
    }

    const handleClose = () => {
        setAddModalOpen(false);
    }

    const [splitOrder, {isLoading: splitOrderIsLoading}] = useSplitOrderMutation();

    // "Yes" on the backorder question: split off every backordered quantity into a new order.
    const handleSplitBackorderConfirm = async () => {
        try {
            if (splitOrderIsLoading) return;

            const resp = await splitOrder({id: props.orderId, products: backorderProducts});
            if (resp.error) {
                showToastMessageRequestError(t("split_order_failed"), t, resp.error);
            } else {
                showToastMessagePostRequest(t, resp.data);
            }
        } catch (err) {
            showToastMessageRequestError(t("split_order_failed"), t, err);
        } finally {
            backorderSplitModal.setOpen(false);
        }
    }
    
    const [updateModalOpen, setUpdateModalOpen] = useState(false);
    const updateModal = {
        open: updateModalOpen,
        setOpen: setUpdateModalOpen
    }

    const handleCloseUpdate = () => {
        updateModal.setOpen(false);
    }
    
    const [addInvoiceProductModalOpen, setAddInvoiceProductModalOpen] = useState(false);
    const addInvoiceProductModal = {
        open: addInvoiceProductModalOpen,
        setOpen: setAddInvoiceProductModalOpen
    }

    const handleInvoiceProductModalClose = () => {
        setAddInvoiceProductModalOpen(false);
    };

    const [returnModalOpen, setReturnModalOpen] = useState(false);
    const returnModal = {
        open: returnModalOpen,
        setOpen: setReturnModalOpen
    }

    const [shipmentModalOpen, setShipmentModalOpen] = useState(false);
    const shipmentModal = {
        open: shipmentModalOpen,
        setOpen: setShipmentModalOpen
    }

    const [claimModalOpen, setClaimModalOpen] = useState(false);
    const claimModal = {
        open: claimModalOpen,
        setOpen: setClaimModalOpen
    }

    const handleClaimClose = () => {
        claimModal.setOpen(false);
    }

    const handleReturnClose = () => {
        returnModal.setOpen(false);
    }

    const handleShipmentClose = () => {
        shipmentModal.setOpen(false);
    }

    // Dropship orders must be verified before shipping: the operator types the last 4 characters
    // of the webshop reference. The action to run once verified is stashed in dropshipProceedRef.
    const [dropshipVerifyModalOpen, setDropshipVerifyModalOpen] = useState(false);
    const dropshipProceedRef = useRef(null);
    const dropshipVerifyModal = {
        open: dropshipVerifyModalOpen,
        setOpen: setDropshipVerifyModalOpen,
        request: (proceed) => {
            dropshipProceedRef.current = proceed;
            setDropshipVerifyModalOpen(true);
        }
    }

    const handleDropshipVerifyClose = () => {
        setDropshipVerifyModalOpen(false);
    }

    const handleDropshipVerifyConfirm = (values) => {
        const expected = (orderData?.webshopreference ?? "").slice(-4).toLowerCase();
        const entered = (values.reference ?? "").trim().toLowerCase();
        if (expected && entered === expected) {
            dropshipProceedRef.current?.();
        } else {
            showToastMessage("error", t("dropship_verify_no_match"));
        }
        dropshipProceedRef.current = null;
    }

    const [editDateModalOpen, setEditDateModalOpen] = useState(false);
    const editDateModal = {
        open: editDateModalOpen,
        setOpen: setEditDateModalOpen
    }

    const handleCloseEditDate = () => {
        editDateModal.setOpen(false);
    }
    

    const [remarkModalOpen, setRemarkModalOpen] = useState(false);
    const remarkModal = {
        open: remarkModalOpen,
        setOpen: setRemarkModalOpen
    }

    const handleRemarkModalClose = () => {
        remarkModal.setOpen(false);
    }

    const handleRemarkChange = (val) => {
        setOrderRemark(val);
    }

    const handleRemarkConfirm = async() => {
        try {
            if (isUpdateWithoutCostsLoading) return;

            const data = await updateWithoutCosts({
                id:props.orderId,
                remark: orderRemark,
            }).unwrap()
            showToastMessageUpdateRequest(t, data);
            handleRemarkModalClose();
        } catch (err) {
            showToastMessageRequestError(t("update_failed"), t, err);
        }

        handleFilterPopoverClose();
    }

    // const [orderToEditData, setOrderToTeEditData] = useState(null);

    const [orderToEditModalOpen, setOrderToEditModalOpen] = useState(false);
    const orderToEditModal = {
        open: orderToEditModalOpen,
        setOpen: setOrderToEditModalOpen
    }

    const handleOrderToEditModalClose = () => {
        orderToEditModal.setOpen(false);
    }

    const [snoozeModalOpen, setSnoozeModalOpen] = useState(false);
    const handleSnoozeModalClose = () => {
        setSnoozeModalOpen(false);
    }

    const handleSnoozeConfirm = (snoozeDate) => {
        saveDate(snoozeDate);
    }

    useEffect(() => {
        if (!isLoading && !isFetching && orderData) {
            // setOrderToTeEditData(orderData);

            setPanel(prevPanel => ({
                ...prevPanel,
                title: orderData.webshopreference,
                icon :defaultIcon,
                styling: {
                    header : {
                        padding:"0px !important",
                    }
                },
                tabColor:getBackgroundColor(orderData?.state),
                actions: <SidePanelActions shouldShowActions={shouldShowActions} shouldShowRelationActions={shouldShowRelationActions}  handleFilterPopoverClick={handleFilterPopoverClick} orderId={props.orderId} state={orderData?.state} setIsExtended={setIsExtendedCallback}/>
            }));
        }
    }, [orderData, isLoading, isFetching]);
   
    const datePlanned = useMemo(() => {
        return orderData?.date_planned && orderData.date_planned !== null
          ? new Date(orderData.date_planned).toLocaleDateString(i18n.language)
          : "-";
      }, [orderData?.date_planned, i18n.language]);

    // An order planned for a later moment without a bol.com handover date shows the planned time on hover
    const showPlannedTime = !orderData?.latestHandoverDateTime
        && !["vvb", "vvb_sameday"].includes(orderData?.deliveryMethod)
        && isDatePlannedInFuture(orderData?.date_planned);

    const withPlannedTime = (element) => {
        if (!showPlannedTime) {
            return element;
        }

        const planned = new Date(orderData.date_planned);

        return (
            <SHQuickAction
                component={
                    <Box
                        sx={{
                            px:1.5,
                            display:"flex",
                            flexDirection:"column"
                        }}
                    >
                        <Text light>{t("date_planned_shipment")}</Text>
                        <Text>
                            {planned.toLocaleDateString(i18n.language)}&nbsp;
                            <b>
                            {planned.toLocaleTimeString(i18n.language, {
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                            </b>
                        </Text>
                    </Box>
                }
            >
                {({anchorEl,onMouseEnter,onMouseLeave}) => (
                    <Box
                        aria-owns={anchorEl ? 'simple-popover' : undefined}
                        aria-haspopup={false}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                        sx={{
                            display:"flex",
                        }}
                    >
                        {element}
                    </Box>
                )}
            </SHQuickAction>
        );
    };

    const deliveryDate = useMemo(() => {
        if (!orderData?.deliveryDate) return null;
        const [year, month, day] = orderData.deliveryDate.split("-");
        return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString(i18n.language);
    }, [orderData?.deliveryDate, i18n.language]);

    const createdAt = useMemo(() => {
  return orderData?.createdAt
    ? new Date(orderData.createdAt).toLocaleString(i18n.language, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    : "-";
}, [orderData?.createdAt, i18n.language]);


    const [updateOrder,{isLoading: isUpdateLoading}] = useUpdateOrderMutation();

    // const config = spOrderDetailsConfig(isRelation);
    const [config,setConfig] = useState(spOrderDetailsConfig(isRelation));
    useEffect(() => {
        setConfig(spOrderDetailsConfig(isRelation));
    },[isRelation])

    const saveDate = async (date) => {
        try {
            const data = await updateOrder({
                id:props.orderId,
                date_planned:date,
            }).unwrap()
            showToastMessageUpdateRequest(t, data);

        } catch (err) {
            // if (err.name === 'ValidationError') {
            //     // Create an errors object where keys are field names and values are error messages
            //     const errors = err.inner.reduce((acc, currentError) => ({
            //         ...acc,
            //         [currentError.path]: currentError.message
            //     }), {});
        
            //     // Set the validation errors in the state
            // } else {
            //     // Handle other errors, such as network errors
            //     showToastMessageRequestError(t("create_failed"), t, err);
            // }
        }
    }

    // Calculate order costs (DEMO FUNCTION)
    // const [setOrderCosts,{isLoading: isSetOrderCostsLoading}] = useSetOrderCostsMutation();
    // const calculateOrderCosts = async () => {
    //     try {
    //         const data = await setOrderCosts(props.orderId).unwrap()
    //         showToastMessageUpdateRequest(t, data);

    //     } catch (err) {
    //         showToastMessageRequestError(t("create_failed"), t, err);
    //     }
    // }
    return (
        isRelation ?
        <>
            {/* {ready ?? */}
                <Sidepanel2
                    defaultTab={props.defaultTab ?? null}
                    isCustom
                    enableUrlViews={false}
                    skeleton={isLoading || isFetching}
                    tabConfig={config}
                    content={panel} 
                    isExtended={props.isExtended} 
                    setIsExtended={props.setIsExtended} >
                    <>
                        <SummaryRelation orderId={props.orderId} config={config.summaryRelation} canReserveOrderLines={props.canReserveOrderLines} data={orderData}/>
                        <ShipOrder config={config.shipOrder} data={orderData}/>
                        <Shipments config={config.spShipments}  data={orderData}/>
                    </>
                    <Box
                        sx={{
                            paddingBottom:3,
                            // position:"relative",
                        }}
                    >
                        <Box
                            sx={{
                                pr:3,
                                display:"flex",
                                justifyContent:"space-between",
                                alignItems:"flex-end",
                            }}
                        >
                        <Box 
                            onClick={() => {
                                if(hasModifyPrivilege && ![general_states.SHIPPED].includes(orderData?.state)) {
                                    updateModal.setOpen(true);
                                }
                            }}
                            sx={{
                                background:getBackgroundColor(orderData?.state,100),
                                pt:3,
                                px:3,
                                pb:2,
                                display:"flex",
                                alignItems:"center",
                                fontSize:21,
                                fontWeight:"bold",
                                fontFamily:"Roboto, sans-serif",
                                color: getColor(orderData?.state,400),
                                borderBottomRightRadius:24,
                                "@media screen and (max-width: 47.9375em)" : {
                                    position:"absolute",
                                    top:0,
                                },
                                "@media screen and (max-width: 566px)" : {
                                    display:"none"
                                },
                                position:"relative",
                                cursor:"pointer",
                                // "&::before": {
                                //     content: '""',
                                //     position: "absolute",
                                //     left: 0,
                                //     top: 0,
                                //     height: '100%',
                                //     width: '100%',
                                //     borderBottomRightRadius:24,
                                //     background: "transparent",
                                //     zIndex: 1,
                                //     pointerEvents: "none",
                                //     transition: hasModifyPrivilege && ![general_states.SHIPPED, general_states.FULLYPICKED, general_states.PICKING_IN_PROGRESS, general_states.CANCELLED].includes(orderData?.state) ? 
                                //         "background 200ms cubic-bezier(0.4, 0, 0.2, 1), border 200ms cubic-bezier(0.4, 0, 0.2, 1)" : "none",
                                // },
                                "&:hover&::before": {
                                    background: hasModifyPrivilege && ![general_states.SHIPPED].includes(orderData?.state) ?"rgba(0,0,0, 0.2)" : "transparent"
                                }
                            }}
                        >
                            {t(getOrderStateData(orderData?.state === general_states.REPLENISH ? general_states.OPEN : orderData?.state, theme, isRelation, orderData?.snoozeWhenAvailable).labeltext)}
                            {orderData && hasModifyPrivilege && ![general_states.SHIPPED, general_states.CANCELLED].includes(orderData?.state) &&
                                <EditIcon sx={{marginLeft:1.5}} />
                            }
                        </Box>
                        {(orderData && hasModifyPrivilege) &&
                            <Box
                                sx={{
                                    pb:1,
                                    "@media screen and (max-width: 47.9375em)" : {
                                        display:"none"
                                    }
                                }}
                            >
                                {(orderData && shouldShowRelationActions) &&
                                    <Box
                                        sx={{
                                            pb:1,
                                            "@media screen and (max-width: 47.9375em)" : {
                                                display:"none"
                                            }
                                        }}
                                    >
                                        <Shbutton
                                            onClick={handleFilterPopoverClick}
                                            className={"group-btn"}
                                            // onClick={props.action}
                                            color={"primaryGrey"}
                                            variant="contained"
                                        >
                                            {t('actions')}
                                        </Shbutton>
                                        <AMOrderSpRelations
                                            id={filterPopoverId}
                                            open={filterPopoverOpen}
                                            anchorEl={filterAnchorEl}
                                            onClose={handleFilterPopoverClose}

                                            addModal={addModal}
                                            returnModal={returnModal}
                                            claimModal={claimModal}
                                            confirmDuplicateModal={confirmDuplicateModal}
                                            confirmPickupModal={confirmPickupModal}
                                            confirmShipmentModal={confirmShipmentModal}
                                            orderData={orderData}
                                            remarkModal={remarkModal}
                                            orderToEditModal={orderToEditModal}
                                            priorityModal={priorityModal}
                                            updateInternalRemarkModal={updateInternalRemarkModal}
                                            eventModal={eventModal}
                                            resyncOrderModal={confirmResyncOrderModal}
                                            freeReturnModal={freeReturnModal}
                                        />
                                    </Box>
                                }
                            </Box>
                        }

                        <Box
                            sx={{
                                display:"unset",
                                "@media screen and (min-width: 47.9375em)" : {
                                    display:"none",
                                }
                            }}
                        >
                            {/* {console.log(strategyData?.state)} */}
                            <SidePanelActions  shouldShowActions={shouldShowActions} shouldShowRelationActions={shouldShowRelationActions}  handleFilterPopoverClick={handleFilterPopoverClick} orderId={props.orderId} state={orderData?.state} setIsExtended={setIsExtendedCallback} />
                        </Box>

                        </Box>

                        <Box
                            sx={{
                                px:3,
                                pt:4,
                                display:"flex",
                                justifyContent:"space-between",
                                alignItems:"center",
                                "@media screen and (max-width: 47.9375em)" : {
                                    flexDirection:"column"
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    "@media screen and (max-width: 47.9375em)" : {
                                        display:"flex",
                                        alignItems:"center",
                                        flexDirection:"column",
                                        mb:3,
                                    }
                                }}
                            >
                                <Box
                                    sx={{

                                        display:"flex",
                                        alignItems:"center",
                                        gap:2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display:"flex",
                                            gap:1,
                                        }}
                                    >
                                        <Subheading> {t("order")} {orderData?.barcode}</Subheading>
                                    </Box>
                                </Box>
                                <Box>
                                        <Text italic>{orderData?.webshopreference}{["low","high"].includes(orderData?.priority) ? ` - ${t("priority")}: ${t(orderData.priority)}` : ""}</Text>
                                    </Box>
                                <Box>
                                    <Text light>{orderData?.webshopcredentials?.description ?? t("self_created")}</Text>
                                </Box>
                                {orderData?.customerReference?.length > 0 && (
                                    <>
                                <Box>
                                    <Text bold>{t("customer_reference")}:</Text>
                                </Box>
                                <Box>
                                    <Text light>{orderData.customerReference}</Text>
                                </Box>
                                </>
                                )}
                            </Box>
    {/* <Subheading */}
                            <Box
                                sx={{
                                    "@media screen and (max-width: 47.9375em)" : {
                                        display:"flex",
                                        // gap:2,
                                        // flexDirection:"column"
                                    },
                                    "@media screen and (max-width: 566px)" : {
                                        width:1,
                                        flexDirection:"column"
                                    }
                                }}
                            >

                                <Box
                                    onClick={() => {
                                        if(hasModifyPrivilege && ![general_states.SHIPPED, general_states.FULLYPICKED, general_states.PICKING_IN_PROGRESS].includes(orderData?.state)) {
                                            updateModal.setOpen(true);
                                        }
                                    }}
                                    sx={{
                                        // height:40,
                                        lineHeight:"40px",
                                        background:getBackgroundColor(orderData?.state,100),
                                        px:3,
                                        fontSize:14,
                                        fontWeight:"bold",
                                        fontFamily:"Roboto, sans-serif",
                                        color: getColor(orderData?.state),
                                        borderRadius:20,
                                        display:"none",
                                        alignItems:"center",
                                        flex:1,
                                        "@media screen and (max-width: 47.9375em)" : {
                                            display:"flex",
                                            borderBottomRightRadius:0,
                                            borderBottomLeftRadius:0,
                                            borderTopLeftRadius:"24px",
                                            borderTopRightRadius:"24px",
                                        },
                                        "@media screen and (max-width: 566px)" : {
                                            justifyContent:"center"
                                        }
                                    }}
                                >
                                    {orderData && hasModifyPrivilege && ![general_states.SHIPPED, general_states.CANCELLED].includes(orderData?.state) &&
                                        <Box sx={{marginLeft:"auto",width:24,}} />
                                    }
                                    {t(getOrderStateData(orderData?.state, theme, false, orderData?.snoozeWhenAvailable).labeltext)}
                                    {orderData && hasModifyPrivilege && ![general_states.SHIPPED, general_states.CANCELLED].includes(orderData?.state) &&
                                        <EditIcon sx={{marginLeft:"auto"}} />
                                    }
                                </Box>
                                <Box
                                    sx={{
                                        display:"flex",
                                        alignItems:"center",
                                        gap:4,
                                        borderRadius:7,
                                        height:56,
                                        px:4,
                                        pl:orderData?.isB2B ? 1 : null,
                                        background:orderData?.isB2B ? colors[getOrderStateData(orderData?.state, theme).labelcolor][100] : theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                        border:orderData?.isB2B ? `1px solid ${colors[getOrderStateData(orderData?.state, theme).labelcolor][400]}` : null,
                                        "@media screen and (max-width: 47.9375em)" : {
                                            borderTopLeftRadius:0,
                                            borderTopRightRadius:0,
                                            borderBottomLeftRadius:"24px",
                                            borderBottomRightRadius:"24px",
                                            // width:400,
                                        },
                                        "@media screen and (max-width: 566px)" : {
                                            flexDirection:"column",
                                            gap:1.5,
                                            height:"unset",
                                            px:3,
                                            py:2,
                                        }
                                    }}
                                >

                                    {orderData?.isB2B &&
                                        <Box
                                            sx={{
                                                height:40,
                                                lineHeight:"40px",
                                                borderRadius:5,
                                                px:2,
                                                fontWeight:500,
                                                fontFamily:"Roboto, sans-serif",
                                                border: `1px solid ${colors[getOrderStateData(orderData?.state, theme).labelcolor][400]}`,
                                                background: colors?.[getOrderStateData(orderData?.state, theme).labelcolor][400],
                                                color: themeSettings(theme.palette.mode).palette[getOrderStateData(orderData?.state, theme).labelcolor].contrastText,
                                                "@media screen and (max-width: 47.9375em)" : {
                                                    order:4,
                                                    width:1,
                                                    textAlign:'center'
                                                }
                                            }}
                                        >
                                            {t("B2B")}
                                        </Box>
                                    }
                                    <Box
                                        sx={{
                                            display:"flex",
                                            flexDirection:"column",
                                            "@media screen and (max-width: 566px)" : {
                                                flexDirection:"row",
                                                width:1,
                                                justifyContent:"space-between",
                                            }
                                        }}
                                    >
                                        <Text light>{t("delivery_name")}</Text>
                                        <Text>{orderData?.deliveryName ?? t("unknown")}</Text>
                                    </Box>
                                    <Box
                                        sx={{
                                            display:"flex",
                                            flexDirection:"column",
                                            "@media screen and (max-width: 566px)" : {
                                                flexDirection:"row",
                                                width:1,
                                                justifyContent:"space-between",
                                            }
                                        }}
                                    >
                                        
                                        <Text sx={{flexShrink:0}} light noBreak>{t("date_planned_shipment")}</Text>
                                        {/* {general_states} */}
                                        {hasModifyPrivilege && ![general_states.SHIPPED, general_states.PICKING_IN_PROGRESS, general_states.FULLYPICKED, general_states.CANCELLED].includes(orderData?.state) ? 
                                           <Box
                                                onClick={() => setSnoozeModalOpen(true)}
                                                sx={{
                                                    height: 20,
                                                    background: theme.palette.mode === "dark" ? colors.grey[300] : colors.bg["tertiary"],
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    width: "100%",
                                                    gap: 2.5,
                                                    paddingX: 2.5,
                                                    borderRadius: 5,
                                                    userSelect: "none",
                                                    transition: "250ms",
                                                    cursor: "pointer",
                                                    "&:hover": {
                                                        background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                                        transition: "250ms",
                                                    }
                                                }}
                                            >
                                                {withPlannedTime(
                                                    <Text noBreak semibold>
                                                        {datePlanned}
                                                    </Text>
                                                )}
                                            </Box>
                                        :
                                            withPlannedTime(<Text>{datePlanned}</Text>)
                                        }
                                    </Box>
                                    {deliveryDate && (
                                        <Box
                                            sx={{
                                                display:"flex",
                                                flexDirection:"column",
                                                "@media screen and (max-width: 566px)" : {
                                                    flexDirection:"row",
                                                    width:1,
                                                    justifyContent:"space-between",
                                                }
                                            }}
                                        >
                                            <Text sx={{flexShrink:0}} light noBreak>{t("delivery_date")}</Text>
                                            <Text>{deliveryDate}</Text>
                                        </Box>
                                    )}
                                    <Box
                                        sx={{
                                            display:"flex",
                                            flexDirection:"column",
                                            "@media screen and (max-width: 566px)" : {
                                                flexDirection:"row",
                                                width:1,
                                                justifyContent:"space-between",
                                            }
                                        }}
                                    >
                                        <Text noBreak light>{t("orderlines_count")}</Text>
                                        <Text>{orderData?.amountOfLines}</Text>
                                    </Box>
                                    <Box
                                        sx={{
                                            display:"flex",
                                            flexDirection:"column",
                                            "@media screen and (max-width: 566px)" : {
                                                flexDirection:"row",
                                                width:1,
                                                justifyContent:"space-between",
                                            }
                                        }}
                                    >
                                        <Text light>{t("created_on")}</Text>
                                        <Text>{createdAt}</Text>
                                    </Box>
                                </Box>
                                
                            </Box>

                        </Box>
                        {orderData?.remark && orderData.remark.length > 0 &&
                            <Box
                                sx={{
                                    mt:3,
                                    mx:3,
                                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[200],
                                    px:1.5,
                                    py:1,
                                    borderRadius:4,
                                }}
                            >
                                <Box>
                                    <Text
                                        light
                                    >
                                        {t("remark")}
                                    </Text>
                                </Box>
                                <Text>
                                    {orderData?.remark}
                                </Text>
                            </Box>
                        }
                        {orderData?.relationRemark && orderData.relationRemark.length > 0 &&
                            <Box
                                sx={{
                                    mt:3,
                                    mx:3,
                                    background:theme.palette.mode === "dark" ? colors.orange[200] : colors.orange[100],
                                    px:1.5,
                                    py:1,
                                    borderRadius:4,
                                }}
                            >
                                <Box>
                                    <Text
                                        light
                                    >
                                        {t("internal_remark")}
                                    </Text>
                                </Box>
                                <Text>
                                    {orderData?.relationRemark}
                                </Text>
                            </Box>
                        }
                        {orderData?.skipReason && orderData?.skipReason.length > 0 && orderData?.state === general_states.ACTION_REQUIRED_CUSTOMER &&
                            
                            <Box
                                sx={{
                                    mt:2,
                                    background:colors.orange[100],
                                    py:1,
                                    px:2,
                                    mr: "24px",
                                    ml: "24px",
                                    borderRadius:10,
                                    border: `1px solid ${colors.orange[400]}`
                                }}
                            >
                                <Text bold>{orderData?.skipReason}</Text>
                            </Box>
                        }
                    </Box>
                </Sidepanel2>
            <MSplitOrder orderId={props.orderId} onOpen={() => addModal.setOpen(true)} onClose={() => addModal.setOpen(false)} open={addModal.open} handleClose={handleClose}/>
            <MSplitBackorder
                open={backorderSplitModal.open}
                isLoading={splitOrderIsLoading}
                handleClose={() => setBackorderSplitModalOpen(false)}
                onYes={handleSplitBackorderConfirm}
                onNo={() => {
                    setBackorderSplitModalOpen(false);
                    setAddModalOpen(true);
                }}
            />
            <MUpdateOrderState orderId={props.orderId} snoozeWhenAvailable={orderData?.snoozeWhenAvailable} deliveryMethod={orderData?.deliveryMethod} state={orderData?.state} previousState={orderData?.previousState} open={updateModal.open} onOpen={() => updateModal.setOpen(true)} onClose={() => updateModal.setOpen(false)} handleClose={handleCloseUpdate}/>
            <MpAddReturn 
                onOpen={() => returnModal.setOpen(true)} 
                onClose={() => returnModal.setOpen(false)} 
                open={returnModal.open} 
                handleClose={handleReturnClose} 
                data={orderData}
            />
            <MAddClaim
                open={claimModal.open}
                handleClose={handleClaimClose}
                data={orderData}
                orderLines={orderLinesData?.data ?? []}
                />
            
            <MEditOrderExtraFields 
                data={orderData} 
                onOpen={() => orderToEditModal.setOpen(true)} 
                onClose={() => orderToEditModal.setOpen(false)} 
                open={orderToEditModal.open} 
                handleClose={handleOrderToEditModalClose}
            />
            
            <MSnoozeOrder
                isRelation={isRelation}
                open={snoozeModalOpen}
                handleClose={handleSnoozeModalClose}
                confirm={handleSnoozeConfirm}
            />
            
            <MpDuplicateOrder
                open={duplicateModal.open}
                handleClose={handleDuplicateClose}
                orderData={orderData}
                onOpenCreatedOrder={handleOpenCreatedOrder}
            />
            {createdOrderId &&
                <SpOrderDetails
                    orderId={createdOrderId}
                    isExtended={createdOrderExtended}
                    setIsExtended={setCreatedOrderExtended}
                />
            }
            <MConfirmation
                open={confirmPickupModal.open}
                handleClose={handleConfirmPickupClose}
                title={t("mc_order_to_pickup_title")}
                subtitle={t("mc_order_to_pickup_subtitle")}
                confirm={() => handleOrderPickup(true)}
            />
            <MConfirmation
                open={confirmShipmentModal.open}
                handleClose={handleConfirmShipmentClose}
                title={t("mc_order_to_ship_title")}
                subtitle={t("mc_order_to_ship_subtitle")}
                confirm={() => handleOrderPickup(false)}
            />
            <MConfirmation
                open={confirmResyncOrderModal.open}
                handleClose={handleResyncOrderClose}
                title={t("mc_order_resync_title")}
                subtitle={t("mc_order_resync_subtitle")}
                confirm={() => handleOrderResync()}
            />
            <MConfirmationWithValue
                open={remarkModal.open}
                handleClose={handleRemarkModalClose}
                confirm={() => handleRemarkConfirm(false)}
                onChange={handleRemarkChange}
                data={{title: t("mc_order_update_remark_title"), value: orderRemark, hasInput: true, inputLabel: t('remark'), inputType: "text"}}
            />
            <MDynamicValues
                open={priorityModal.open}
                handleClose={() => priorityModal.setOpen(false)}
                title={t("update_order_priority")}
                labelWidth={80}
                fields={[
                    { name: "priority", label: t("priority"), explainerTitle: t("priority"),explainerSubTitle: t("update_order_priority_subtitle"),type: "list", options: [{id: "low", name: t('low')},{id: "high", name: t('high')}] }
                ]}
                onConfirm={(values) => handleUpdatePriority(values)}
            />
            <MDynamicValues
                open={eventModal.open}
                handleClose={() => eventModal.setOpen(false)}
                title={t("add_event")}
                subtitle={isRelation ? t("event_internal_subtitle") : null}
                labelWidth={120}
                fields={[
                    { name: "message", label: t("description"), type: "text", required: true },
                ]}
                onConfirm={(values) => handleCreateOrderEvent(values)}
            />

            <MDynamicValues
                open={updateInternalRemarkModal.open}
                handleClose={() => updateInternalRemarkModal.setOpen(false)}
                title={t("update_internal_remark")}
                labelWidth={80}
                fields={[
                    { name: "relationRemark", label: t("remark"), type: "text" },
                ]}
                onConfirm={(values) => handleUpdateInternalRemark(values)}
            />
            <MConfirmation
                open={freeReturnModal.open}
                handleClose={handleFreeReturnClose}
                title={orderData?.freeReturn ? t("mc_order_paid_return_title") : t("mc_order_free_return_title")}
                subtitle={orderData?.freeReturn ? t("mc_order_paid_return_subtitle") : t("mc_order_free_return_subtitle")}
                confirm={() => handleFreeReturn()}
            />
        </>
        :
        <>
            <Sidepanel2
                width={1080}
                defaultTab={props.defaultTab ?? null}
                isCustom
                enableUrlViews={false}
                skeleton={isLoading || isFetching}
                tabConfig={config}
                content={panel} 
                isExtended={props.isExtended} 
                setIsExtended={props.setIsExtended} >
                <>
                    <Summary config={config.summary}  data={orderData} orderId={props.orderId}/>
                    <PickingLines config={config.pickingLines} data={orderData} />
                    <ShipOrder config={config.shipOrder}  data={orderData}/>
                    <Shipments config={config.spShipments}  data={orderData}/>
                </>
                <Box
                    sx={{
                        paddingBottom:3,
                        // position:"relative",
                    }}
                >
                    <Box
                        sx={{
                            pr:3,
                            display:"flex",
                            justifyContent:"space-between",
                            alignItems:"flex-end",
                        }}
                    >
                        <Box 
                            onClick={() => {
                                if(((hasModifyPrivilege && ![general_states.SHIPPED, general_states.FULLYPICKED, general_states.PICKING_IN_PROGRESS].includes(orderData?.state)) ||
                                (hasCancelPickedPrivilege && [general_states.FULLYPICKED, general_states.PICKING_IN_PROGRESS].includes(orderData?.state)) || 
                                (hasModifyShippedPrivilege && [general_states.SHIPPED].includes(orderData?.state)))) {
                                    updateModal.setOpen(true);
                                }
                            }}
                            sx={{
                                background:getBackgroundColor(orderData?.state,100),
                                // pt:isDatePlannedInFuture(orderData?.date_planned) && orderData?.state === general_states.OPEN ? 1: 3,
                                px:3,
                                pt:3,
                                pb:2,
                                mb:isDatePlannedInFuture(orderData?.date_planned) && orderData?.state === general_states.OPEN ? 3: 0,
                                display:"flex",
                                alignItems:"center",
                                fontSize:21,
                                fontWeight:"bold",
                                fontFamily:"Roboto, sans-serif",
                                color: getColor(orderData?.state),

                                borderBottomRightRadius:isDatePlannedInFuture(orderData?.date_planned) && orderData?.state === general_states.OPEN ? 0: 24,
                                "@media screen and (max-width: 47.9375em)" : {
                                    position:"absolute",
                                    top:0,
                                },
                                "@media screen and (max-width: 566px)" : {
                                    display:"none"
                                },
                                position:"relative",
                                cursor:"pointer",
                                flexWrap:"wrap",
                                "&::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    height: '100%',
                                    width: '100%',
                                    borderBottomRightRadius:isDatePlannedInFuture(orderData?.date_planned) && orderData?.state === general_states.OPEN ? 0: 24,
                                    background: "transparent",
                                    zIndex: 1,
                                    pointerEvents: "none",
                                    transition: ![general_states.SHIPPED, general_states.FULLYPICKED, general_states.PICKING_IN_PROGRESS].includes(orderData?.state) ? 
                                    "background 200ms cubic-bezier(0.4, 0, 0.2, 1), border 200ms cubic-bezier(0.4, 0, 0.2, 1)" : "none",
                                },
                                "&:hover&::before": {
                                    background: ![general_states.SHIPPED, general_states.FULLYPICKED, general_states.PICKING_IN_PROGRESS].includes(orderData?.state) ?
                                    "rgba(0,0,0, 0.2)" : "transparent"
                                }
                            }}
                        >
                            {t(getOrderStateData(orderData?.state, theme, false, orderData?.snoozeWhenAvailable).labeltext)}

                            {orderData && ((hasModifyPrivilege && ![general_states.SHIPPED, general_states.FULLYPICKED, general_states.PICKING_IN_PROGRESS].includes(orderData?.state)) ||
                                (hasCancelPickedPrivilege && [general_states.FULLYPICKED, general_states.PICKING_IN_PROGRESS].includes(orderData?.state))) &&
                                <EditIcon sx={{marginLeft:1.5}} />
                            }

                            {isDatePlannedInFuture(orderData?.date_planned) && orderData?.state === general_states.OPEN && 
                                <Box 
                                    sx={{
                                        width:1,
                                        // px:2,
                                        left:0,
                                        background:"#000",
                                        height:30,
                                        mr:"-12px",
                                        position:'absolute',
                                        bottom:-24,
                                        background: themeSettings(theme.palette.mode).palette[getOrderStateData(orderData?.state, theme).labelcolor]?.main,
                                        color:themeSettings(theme.palette.mode).palette[getOrderStateData(orderData?.state, theme).labelcolor]?.contrastText,
                                        fontSize:14,
                                        borderBottomRightRadius:24,
                                        pl:3,
                                        lineHeight:"30px",
                                    }}
                                >
                                    {t("postponed")}
                                </Box>
                            }
                        </Box>
                        {(orderData && shouldShowActions) &&
                            <Box
                                sx={{
                                    pb:1,
                                    mb:isDatePlannedInFuture(orderData?.date_planned) && orderData?.state === general_states.OPEN ? 3: 0,
                                    "@media screen and (max-width: 47.9375em)" : {
                                        display:"none"
                                    }
                                }}
                            >
                                {orderData.withoutCosts && (
                                <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle", mr: 1 }}>
                                    <ShLabel size={32} fitted palette="normal" variant="orange">
                                        {t('order_no_costs_label')}
                                    </ShLabel>
                                </Box>
                                )}
                                <Shbutton
                                    onClick={handleFilterPopoverClick}
                                    className={"group-btn"}
                                    // onClick={props.action}
                                    color={"primaryGrey"}
                                    variant="contained"
                                >
                                    {t('actions')}
                                </Shbutton>
                                <AMOrderSpWarehouses
                                    id={filterPopoverId}
                                    open={filterPopoverOpen}
                                    anchorEl={filterAnchorEl}
                                    onClose={handleFilterPopoverClose}

                                    addModal={addModal}
                                    returnModal={returnModal}
                                    claimModal={claimModal}
                                    confirmDuplicateModal={confirmDuplicateModal}
                                    shipmentModal={shipmentModal}
                                    dropshipVerifyModal={dropshipVerifyModal}
                                    confirmPickupModal={confirmPickupModal}
                                    confirmPickFromBulkModal={confirmPickFromBulkModal}
                                    confirmShipmentModal={confirmShipmentModal}
                                    orderData={orderData}
                                    addInvoiceProductModal={addInvoiceProductModal}
                                    confirmNoCostOrderModal={confirmNoCostOrderModal}
                                    remarkModal={remarkModal}
                                    resyncOrderModal={confirmResyncOrderModal}
                                    orderToEditModal={orderToEditModal}
                                    priorityModal={priorityModal}
                                    eventModal={eventModal}
                                    setToShippedModal={setToShippedModal}
                                    checkModal={checkModal}
                                    checkCostRuleModal={checkCostRuleModal}
                                    webshopJsonModal={webshopJsonModal}
                                   // calculateOrderCosts={calculateOrderCosts}
                                
                                />

                            </Box>
                        }

                        <Box
                            sx={{
                                display:"unset",
                                "@media screen and (min-width: 47.9375em)" : {
                                    display:"none",
                                }
                            }}
                        >
                            {/* {console.log(strategyData?.state)} */}
                            <SidePanelActions shouldShowActions={shouldShowActions} shouldShowRelationActions={shouldShowRelationActions} withoutCosts={orderData?.withoutCosts} handleFilterPopoverClick={handleFilterPopoverClick} orderId={props.orderId} state={orderData?.state} setIsExtended={setIsExtendedCallback} />
                        </Box>

                    </Box>

                    <Box
                        sx={{
                            px:3,
                            pt:4,
                            display:"flex",
                            flexDirection:"column"
                        }}
                    >
                        <Box
                            sx={{
                                display:"flex",
                                justifyContent:"space-between",
                                alignItems:"center",
                                "@media screen and (max-width: 47.9375em)" : {
                                    flexDirection:"column"
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    "@media screen and (max-width: 47.9375em)" : {
                                        display:"flex",
                                        alignItems:"center",
                                        flexDirection:"column",
                                        mb:3,
                                    }
                                }}
                            >
                                <Box
                                    sx={{

                                        display:"flex",
                                        alignItems:"center",
                                        gap:2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display:"flex",
                                            gap:1,
                                            "@media screen and (max-width: 47.9375em)" : {
                                                flexDirection:"column",
                                                gap:0,
                                                alignItems:"center",
                                            }
                                        }}
                                    >
                                        <Subheading variant={getOrderStateData(orderData?.state, theme).labelcolor} >{`${t("order")} ${orderData?.barcode}`}</Subheading>
                                        <Box
                                            sx={{
                                                display:"flex",
                                                gap:1,
                                            }}
                                        >
                                            <Text italic>({orderData?.webshopreference}){["low","high"].includes(orderData?.priority) ? ` - ${t("priority")}: ${t(orderData.priority)}` : ""}</Text>
                                            <Text light
                                                sx={{
                                                    "@media screen and (min-width: 47.9375em)" : {
                                                        display:"none",
                                                    }
                                                }}
                                            >
                                                {t(orderData?.relation?.name ?? t("unknown"))}
                                            </Text>
                                            <Text
                                                light
                                                sx={{
                                                    "@media screen and (min-width: 47.9375em)" : {
                                                        display:"none",
                                                    }
                                                }}
                                            >
                                                ({orderData?.webshopcredentials?.description ?? t("self_created")})
                                            </Text>
                                        </Box>
                                    </Box>

                                </Box>
                                {/* <Box>
                                    <Text italic>{orderData?.webshopreference}</Text>
                                </Box> */}
                                <Box
                                    sx={{
                                        display:"flex",
                                        // alignItems:"center",
                                        flexDirection:"column",
                                        "@media screen and (max-width: 47.9375em)" : {
                                            alignItems:"center",
                                            display:'none',
                                        }
                                    }}
                                >
                                    <Text>{t(orderData?.relation?.name ?? t("unknown"))}</Text> 
                                    <Text light italic>({orderData?.webshopcredentials?.description ?? t("self_created")})</Text>
                                </Box>
                              
                                
                            </Box>
    {/* <Subheading */}
                            <Box
                                sx={{
                                    "@media screen and (max-width: 47.9375em)" : {
                                        display:"flex",
                                        // gap:2,
                                        // flexDirection:"column"
                                    },
                                    "@media screen and (max-width: 566px)" : {
                                        width:1,
                                        flexDirection:"column"
                                    }
                                }}
                            >
                               
                                <Box
                                    onClick={() => {
                                        
                                        if(((hasModifyPrivilege && ![general_states.SHIPPED, general_states.FULLYPICKED, general_states.PICKING_IN_PROGRESS].includes(orderData?.state)) ||
                                        (hasCancelPickedPrivilege && [general_states.FULLYPICKED, general_states.PICKING_IN_PROGRESS].includes(orderData?.state)))) {
                                            updateModal.setOpen(true);
                                        }
                                    }}
                                    sx={{
                                        // height:40,
                                        lineHeight:"40px",
                                        background:getBackgroundColor(orderData?.state,100),
                                        px:3,
                                        fontSize:14,
                                        fontWeight:"bold",
                                        fontFamily:"Roboto, sans-serif",
                                        color: getColor(orderData?.state),
                                        borderRadius:20,
                                        display:"none",
                                        alignItems:"center",
                                        flex:1,
                                        "@media screen and (max-width: 47.9375em)" : {
                                            display:"flex",
                                            borderBottomRightRadius:0,
                                            borderBottomLeftRadius:0,
                                            borderTopLeftRadius:"24px",
                                            borderTopRightRadius:"24px",
                                            background:orderData?.isB2B ? getBackgroundColor(orderData?.state,200) :  null,
                                        },
                                        "@media screen and (max-width: 566px)" : {
                                            justifyContent:"center"
                                        }
                                    }}
                                >
                                    
                                    {orderData && ((hasModifyPrivilege && ![general_states.SHIPPED, general_states.FULLYPICKED, general_states.PICKING_IN_PROGRESS].includes(orderData?.state)) ||
                                    (hasCancelPickedPrivilege && [general_states.FULLYPICKED, general_states.PICKING_IN_PROGRESS].includes(orderData?.state))) &&
                                        <Box sx={{marginLeft:"auto",width:24,}} />
                                    }
                                    
                                    {t(getOrderStateData(orderData?.state, theme, false, orderData?.snoozeWhenAvailable).labeltext)}
                                    
                                    {orderData && ((hasModifyPrivilege && ![general_states.SHIPPED, general_states.FULLYPICKED, general_states.PICKING_IN_PROGRESS].includes(orderData?.state)) ||
                                    (hasCancelPickedPrivilege && [general_states.FULLYPICKED, general_states.PICKING_IN_PROGRESS].includes(orderData?.state))) &&
                                        <EditIcon sx={{marginLeft:"auto"}} />
                                    }
                                </Box>
                                <Box
                                    sx={{
                                        display:"flex",
                                        alignItems:"center",
                                        gap:4,
                                        borderRadius:7,
                                        height:56,
                                        px:4,
                                        pl:orderData?.isB2B ? 1 : null,
                                        background:orderData?.isB2B ? colors[getOrderStateData(orderData?.state, theme).labelcolor][100] : theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                        border:orderData?.isB2B ? `1px solid ${colors[getOrderStateData(orderData?.state, theme).labelcolor][400]}` : null,
                                        "@media screen and (max-width: 47.9375em)" : {
                                            borderTopLeftRadius:0,
                                            borderTopRightRadius:0,
                                            borderBottomLeftRadius:"24px",
                                            borderBottomRightRadius:"24px",
                                            // width:400,
                                        },
                                        "@media screen and (max-width: 566px)" : {
                                            flexDirection:"column",
                                            gap:2,
                                            height:"unset",
                                            px:3,
                                            py:2,
                                            border:"none",
                                        }
                                    }}
                                >
                                    {orderData?.isB2B &&
                                        <Box
                                            sx={{
                                                height:40,
                                                lineHeight:"40px",
                                                borderRadius:5,
                                                px:2,
                                                fontWeight:500,
                                                fontFamily:"Roboto, sans-serif",
                                                border: `1px solid ${colors[getOrderStateData(orderData?.state, theme).labelcolor][400]}`,
                                                background: colors?.[getOrderStateData(orderData?.state, theme).labelcolor][400],
                                                color: themeSettings(theme.palette.mode).palette[getOrderStateData(orderData?.state, theme).labelcolor].contrastText,
                                                "@media screen and (max-width: 47.9375em)" : {
                                                    order:4,
                                                    width:1,
                                                    textAlign:'center'
                                                }
                                            }}
                                        >
                                            {t("B2B")}
                                        </Box>
                                    }
                                    <Box
                                        sx={{
                                            display:"flex",
                                            flexDirection:"column",
                                            "@media screen and (max-width: 566px)" : {
                                                flexDirection:"row",
                                                width:1,
                                                justifyContent:"space-between",
                                                gap:2,
                                            }
                                        }}
                                    >
                                        <Text noBreak light>{t("orderlines_count")}</Text>
                                        <Text>{orderData?.amountOfLines ?? "-"}</Text>
                                    </Box>
                                    <Box
                                        sx={{
                                            display:"flex",
                                            flexDirection:"column",
                                            "@media screen and (max-width: 566px)" : {
                                                flexDirection:"row",
                                                width:1,
                                                justifyContent:"space-between",
                                                alignItems:'center',
                                                gap:2,
                                            }
                                        }}
                                    >
                                        {orderData?.latestHandoverDateTime ? 
                                        <SHQuickAction
                                            component={
                                                <Box
                                                    sx={{
                                                        px:1.5,
                                                        display:"flex",
                                                        flexDirection:"column"
                                                    }}
                                                >
                                                    <Text light>{t("handover_date")}</Text>
                                                    <Text>
                                                        { new Date(orderData.latestHandoverDateTime).toLocaleDateString(i18n.language)}&nbsp;
                                                        <b>
                                                        {new Date(orderData.latestHandoverDateTime).toLocaleTimeString(i18n.language, {
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                        </b>
                                                    </Text>
                                                </Box>
                                            }
                                        >
                                            {({anchorEl,onMouseEnter,onMouseLeave,onTouchStart}) => (
                                                <Box
                                                    aria-owns={anchorEl ? 'simple-popover' : undefined}
                                                    aria-haspopup={false}
                                                    onMouseEnter={orderData?.latestHandoverDateTime !== null ? onMouseEnter : null}
                                                    onMouseLeave={orderData?.latestHandoverDateTime !== null ? onMouseLeave : null}
                                                    onTouchStart={orderData?.latestHandoverDateTime !== null ? onTouchStart : null}
                                                    sx={{
                                                        width:1,
                                                    }}
                                                >
                                                    <Text sx={{flexShrink:0}} light noBreak>{t("date_planned_shipment")}</Text>
                                                </Box>
                                            )}
                                        </SHQuickAction>
                                        :
                                        (<Text sx={{flexShrink:0}} light noBreak>{t("date_planned_shipment")}</Text>)}


                                        {hasModifyPrivilege && ![general_states.SHIPPED, general_states.PICKING_IN_PROGRESS, general_states.FULLYPICKED, general_states.CANCELLED].includes(orderData?.state) ?
                                            <Box
                                                sx={{
                                                    width:1,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width:1,
                                                        display:"unset",
                                                        "@media screen and (max-width: 566px)" : {
                                                            display:"none"
                                                        }

                                                    }}
                                                >
                                                    <Box
                                                        onClick={() => setSnoozeModalOpen(true)}
                                                        sx={{
                                                            height: 20,
                                                            background: theme.palette.mode === "dark" ? colors.grey[300] : colors.bg["tertiary"],
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            width: "100%",
                                                            gap: 2.5,
                                                            paddingX: 2.5,
                                                            borderRadius: 5,
                                                            userSelect: "none",
                                                            transition: "250ms",
                                                            cursor: "pointer",
                                                            "&:hover": {
                                                                background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                                                transition: "250ms",
                                                            }
                                                        }}
                                                    >
                                                        {withPlannedTime(
                                                            <Text noBreak semibold>
                                                                {datePlanned}
                                                            </Text>
                                                        )}
                                                    </Box>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        width:1,
                                                        display:"none",
                                                        "@media screen and (max-width: 566px)" : {
                                                            display:"unset"
                                                        }

                                                    }}
                                                >
                                                    <Box
                                                        onClick={() => setSnoozeModalOpen(true)}
                                                        sx={{
                                                            height: 32,
                                                            background: theme.palette.mode === "dark" ? colors.grey[300] : colors.bg["tertiary"],
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            width: "100%",
                                                            gap: 2.5,
                                                            paddingX: 2.5,
                                                            borderRadius: 5,
                                                            userSelect: "none",
                                                            transition: "250ms",
                                                            cursor: "pointer",
                                                            "&:hover": {
                                                                background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                                                transition: "250ms",
                                                            }
                                                        }}
                                                    >
                                                        {withPlannedTime(
                                                            <Text noBreak semibold>
                                                                {datePlanned}
                                                            </Text>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        :
                                            withPlannedTime(<Text>{datePlanned}</Text>)
                                        }

                                    </Box>
                                    {deliveryDate && (
                                        <Box
                                            sx={{
                                                display:"flex",
                                                flexDirection:"column",
                                                "@media screen and (max-width: 566px)" : {
                                                    flexDirection:"row",
                                                    width:1,
                                                    justifyContent:"space-between",
                                                }
                                            }}
                                        >
                                            <Text sx={{flexShrink:0}} light noBreak>{t("delivery_date")}</Text>
                                            <Text>{deliveryDate}</Text>
                                        </Box>
                                    )}
                                    <Box
                                        sx={{
                                            display:"flex",
                                            flexDirection:"column",
                                            "@media screen and (max-width: 566px)" : {
                                                flexDirection:"row",
                                                width:1,
                                                justifyContent:"space-between",
                                            }
                                        }}
                                    >
                                        <Text light>{t("stream")}</Text>
                                        <Text noBreak>{orderData?.stream ? orderData?.stream?.shortName : "-"}</Text>
                                    </Box>
                                    
                                </Box>
                                
                            </Box>
                        </Box>

                        {orderData?.skipReason &&
                            
                            <Box
                                sx={{
                                    mt:2,
                                    background:colors.orange[100],
                                    py:1,
                                    px:2,
                                    borderRadius:10,
                                    border: `1px solid ${colors.orange[400]}`
                                }}
                            >
                                <Text bold>{orderData?.skipReason}</Text>
                            </Box>
                        }

                    </Box>
                </Box>
            </Sidepanel2>
            <MSplitOrder
                orderId={props.orderId}
                onOpen={() => addModal.setOpen(true)}
                onClose={() => addModal.setOpen(false)}
                open={addModal.open}
                handleClose={handleClose}
            />
            <MSplitBackorder
                open={backorderSplitModal.open}
                isLoading={splitOrderIsLoading}
                handleClose={() => setBackorderSplitModalOpen(false)}
                onYes={handleSplitBackorderConfirm}
                onNo={() => {
                    setBackorderSplitModalOpen(false);
                    setAddModalOpen(true);
                }}
            />

            <MEditOrderExtraFields
                data={orderData} 
                onOpen={() => orderToEditModal.setOpen(true)} 
                onClose={() => orderToEditModal.setOpen(false)} 
                open={orderToEditModal.open} 
                handleClose={handleOrderToEditModalClose}
            />
            <MUpdateOrderState orderId={props.orderId} snoozeWhenAvailable={orderData?.snoozeWhenAvailable} state={orderData?.state} deliveryMethod={orderData?.deliveryMethod} previousState={orderData?.previousState} open={updateModal.open} onOpen={() => updateModal.setOpen(true)} onClose={() => updateModal.setOpen(false)} handleClose={handleCloseUpdate}/>
            <MpAddReturn 
                onOpen={() => returnModal.setOpen(true)} 
                onClose={() => returnModal.setOpen(false)} 
                open={returnModal.open} 
                handleClose={handleReturnClose} 
                data={orderData}
            />
            <MAddShipment
                onOpen={() => shipmentModal.setOpen(true)}
                onClose={() => shipmentModal.setOpen(false)}
                open={shipmentModal.open}
                handleClose={handleShipmentClose}
                data={orderData}
                />
            <MAddClaim
                open={claimModal.open}
                handleClose={handleClaimClose}
                data={orderData}
                orderLines={orderLinesData?.data ?? []}
                />
            <MDynamicValues
                open={dropshipVerifyModal.open}
                handleClose={handleDropshipVerifyClose}
                title={t("dropship_verify_title")}
                subtitle={t("dropship_verify_subtitle")}
                labelWidth={80}
                fields={[
                    { name: "reference", label: t("dropship_verify_reference"), type: "text", required: true },
                ]}
                onConfirm={handleDropshipVerifyConfirm}
            />
            <MSnoozeOrder
                isRelation={isRelation}
                open={snoozeModalOpen}
                handleClose={handleSnoozeModalClose}
                confirm={handleSnoozeConfirm}
            />
            <MpDuplicateOrder
                open={duplicateModal.open}
                handleClose={handleDuplicateClose}
                orderData={orderData}
                onOpenCreatedOrder={handleOpenCreatedOrder}
            />
            {createdOrderId &&
                <SpOrderDetails
                    orderId={createdOrderId}
                    isExtended={createdOrderExtended}
                    setIsExtended={setCreatedOrderExtended}
                />
            }
            <MConfirmation
                open={confirmPickupModal.open}
                handleClose={handleConfirmPickupClose}
                title={t("mc_order_to_pickup_title")}
                subtitle={t("mc_order_to_pickup_subtitle")}
                confirm={() => handleOrderPickup(true)}
            />
            <MConfirmation
                open={confirmPickFromBulkModal.open}
                handleClose={handleConfirmPickFromBulkClose}
                title={t("mc_pick_from_bulk_title")}
                subtitle={t("mc_pick_from_bulk_subtitle")}
                confirm={() => handlePickFromBulk(true)}
            />
            <MConfirmation
                open={confirmShipmentModal.open}
                handleClose={handleConfirmShipmentClose}
                title={t("mc_order_to_ship_title")}
                subtitle={t("mc_order_to_ship_subtitle")}
                confirm={() => handleOrderPickup(false)}
            />
            <MConfirmation
                open={confirmShipmentModal.open}
                handleClose={handleConfirmShipmentClose}
                title={t("mc_order_to_ship_title")}
                subtitle={t("mc_order_to_ship_subtitle")}
                confirm={() => handleOrderPickup(false)}
            />
            <MConfirmation
                open={confirmNoCostOrderModal.open}
                handleClose={handleConfirmNoCostOrderModalClose}
                title={t("mc_order_no_costs_title")}
                subtitle={t("mc_order_no_costs_subtitle")}
                confirm={() => handleUupdateWithoutCosts(false)}
            />
            <MConfirmation
                open={confirmResyncOrderModal.open}
                handleClose={handleResyncOrderClose}
                title={t("mc_order_resync_title")}
                subtitle={t("mc_order_resync_subtitle")}
                confirm={() => handleOrderResync()}
            />
            <MConfirmation
                open={setToShippedModal.open}
                handleClose={handleSetToShippedModalClose}
                title={t("set_order_to_shipped")}
                content={t("mc_set_order_to_shipped_subtitle")}
                confirm={() => handleSetOrderToShipped(false)}
            />
            <MConfirmationWithValue
                open={remarkModal.open}
                handleClose={handleRemarkModalClose}
                confirm={() => handleRemarkConfirm(false)}
                onChange={handleRemarkChange}
                data={{title: t("mc_order_update_remark_title"), value: orderRemark, hasInput: true, inputLabel: t('remark'), inputType: "text"}}
            />
            <MDynamicValues
                open={priorityModal.open}
                handleClose={() => priorityModal.setOpen(false)}
                title={t("update_order_priority")}
                labelWidth={80}
                fields={[
                    { name: "priority", label: t("priority"), explainerTitle: t("priority"),explainerSubTitle: t("update_order_priority_subtitle"),type: "list", options: [{id: "low", name: t('low')},{id: "high", name: t('high')}] }
                ]}
                onConfirm={(values) => handleUpdatePriority(values)}
            />
            <MDynamicValues
                open={eventModal.open}
                handleClose={() => eventModal.setOpen(false)}
                title={t("add_event")}
                subtitle={isRelation ? t("event_internal_subtitle") : null}
                labelWidth={120}
                fields={[
                    { name: "message", label: t("description"), type: "text", required: true },
                ]}
                onConfirm={(values) => handleCreateOrderEvent(values)}
            />

            <MCheckOrderInvoiceProducts
                open={checkModal.open}
                handleClose={handleCheckModalClose}
                orderIds={selectedOrders}
                config={config}
            />

            <MCheckCostRule
                open={checkCostRuleModal.open}
                handleClose={handleCheckCostRuleModalClose}
                orderData={orderData}
            />

            <MOrderWebshopJson
                open={webshopJsonModal.open}
                handleClose={() => webshopJsonModal.setOpen(false)}
                orderData={orderData}
            />

            <MAddInvoiceLine onOpen={() => addInvoiceProductModal.setOpen(true)} linkedObject={{type: "order", id: orderData?.id}} relationId={orderData?.relationId} invoiceId={null} onClose={() => addInvoiceProductModal.setOpen(false)} open={addInvoiceProductModal.open} handleClose={setAddInvoiceProductModalOpen} />
            <MLoader
                open={loaderModal.open}
                message={t("loader_order_to_bulk")}
            />
            {/* <MEditDatePlanned orderId={props.orderId} date_planned={orderData?.date_planned} open={editDateModal.open} onOpen={() => editDateModal.setOpen(true)} onClose={() => editDateModal.setOpen(false)} handleClose={handleCloseEditDate}/> */}
        </>
    );
};

export default SpOrderDetails;